import type { RowData, Table } from '../tanstackTypes';

/** One column the table is showing, and where its value comes from. */
export type TableViewColumn = {
    id: string;
    label: string;
    /** `text`, `number`, `enum`, `date`, `monetaryValue`, … */
    type: string;
    /** The row fields this column reads. Absent when its data is not on the row. */
    fields?: string[];
    /** In words, what the column does with those fields. Absent when it simply reads the one field. */
    derivation?: string;
    /** Every value this column can take, for a column with a fixed set. */
    options?: string[];
    /** False when the value is not on the row at all, and `reason` says where it lives. */
    available: boolean;
    reason?: string;
};

export type TableViewFilter = {
    /** The `id` of the column in `columns` this applies to. */
    column: string;
    filterFn: string;
    value: unknown;
};

export type TableViewSort = {
    column: string;
    direction: 'asc' | 'desc';
};

/**
 * Everything needed to rebuild what a table is showing, without its rows.
 *
 * For handing a view to something that will fetch the data itself. `columns`
 * carries the view's shape, which an export needs as much as it needs the filters.
 *
 * Filters and sorting reference `columns` by id rather than standing alone, so a
 * consumer resolves a column's value once and uses it for all three.
 */
export type TableView = {
    columns: TableViewColumn[];
    filters: TableViewFilter[];
    sort: TableViewSort[];
    search?: { text: string; columns: string[] };
};

// Set by TanStack when a column was declared with a string accessor, and then it is
// the row path that column reads.
function accessorKeyOf<TData extends RowData>(table: Table<TData>, columnId: string): string | undefined {
    const columnDef = table.getColumn(columnId)?.columnDef as { accessorKey?: unknown } | undefined;
    return typeof columnDef?.accessorKey === 'string' ? columnDef.accessorKey : undefined;
}

function describeColumn<TData extends RowData>(table: Table<TData>, columnId: string): TableViewColumn {
    const column = table.getColumn(columnId);
    const meta = column?.columnDef.meta;
    const label = meta?.label() ?? columnId;
    const type = meta?.dataType ?? 'generic';
    // Only some filter configs carry a fixed option set, so this reads it off the
    // shape rather than the union.
    const options = (meta?.filterConfig as { options?: unknown } | undefined)?.options;
    const withOptions = Array.isArray(options) && options.every((option) => typeof option === 'string');

    const base = {
        id: columnId,
        label,
        type,
        ...(withOptions ? { options: options as string[] } : {}),
    };

    const source = meta?.source;
    if (source !== undefined && 'unavailable' in source) {
        return { ...base, available: false, reason: source.unavailable };
    }
    if (source !== undefined) {
        return { ...base, fields: source.fields, derivation: source.derivation, available: true };
    }

    const accessorKey = accessorKeyOf(table, columnId);
    if (accessorKey !== undefined) {
        return { ...base, fields: [accessorKey], available: true };
    }
    // No accessor field and nothing declared, so nothing truthful can be said about
    // where the value comes from. Claiming it is available would hand a consumer a
    // column it cannot fill and no way to know that.
    return { ...base, available: false, reason: 'this column computes its value and has not declared how' };
}

/**
 * Describes what a table is showing, so something else can rebuild it.
 *
 * Columns are the visible ones in display order — the view as the user has arranged
 * it, not every column the table could show.
 */
export function describeTableView<TData extends RowData>(table: Table<TData>): TableView {
    const { columnFilters, sorting, globalFilter } = table.store.state;
    const columns = table.getVisibleLeafColumns().map((column) => describeColumn(table, column.id));
    const searchText = typeof globalFilter === 'string' ? globalFilter : '';

    return {
        columns,
        filters: columnFilters.map((filter) => {
            const { filterFn, value } = (filter.value ?? {}) as { filterFn?: unknown; value?: unknown };
            return {
                column: filter.id,
                filterFn: typeof filterFn === 'string' ? filterFn : 'unknown',
                value,
            };
        }),
        sort: sorting.map((sort) => ({
            column: sort.id,
            direction: sort.desc ? ('desc' as const) : ('asc' as const),
        })),
        // Which columns the box searches, so a consumer matches the same text against
        // the same places rather than every field it can find.
        ...(searchText === '' ? {} : { search: { text: searchText, columns: columns.map((c) => c.id) } }),
    };
}
